import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { UPDATE_STATE } from "../../actions/types";

import {
  Box,
  Collapse,
  Grid,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
} from "@mui/material/";
import "./style.css";

function SettingsSideMenu(props) {
  const { app, auth } = useSelector((state) => state);
  const { ui_state } = app.current_state;
  const { settingsActiveIndex } = ui_state;
  const { settingsSideMenuList } = props;
  const dispatch = useDispatch();

  useEffect(() => {
    localStorage.setItem("isChanged", false);
  }, []);

  const { user } = auth;
  const { user_login_type } = user;
  const [hoveredMenu, setHoveredMenu] = useState(null);
  const [pinnedMenu, setPinnedMenu] = useState(null);
  const [isMouseInSubmenu, setIsMouseInSubmenu] = useState(false);
  const [expandedItems, setExpandedItems] = useState({ markets: true });
  const toggleExpand = (itemId) => {
    setExpandedItems(prev => ({ ...prev, [itemId]: !prev[itemId] }));
  };
  // const selectedMenu = pinnedMenu || findMenuIDByIndex();
  // const activeMenu = hoveredMenu || findMenuIDByIndex();
  const activeMenuFromState = ui_state?.active_settings_menu || findMenuIDByIndex();
  const selectedMenu = pinnedMenu || activeMenuFromState;
  const activeMenu = hoveredMenu || activeMenuFromState;

  useEffect(() => {
    if (ui_state.active_settings_menu) {
      const activeMenuObj = settingsSideMenuList.find(
        (m) => m.menuId === ui_state.active_settings_menu
      );

      // Find first accessible submenu item
      const firstAccessibleItem = activeMenuObj?.data?.find(
        (item) =>
          !item.access ||
          item.access.includes(auth.user.user_login_type)
      );

      // If current index doesn't belong to this menu, reset it to first item
      // Also check subItems for expandable items (e.g. Benchmarking > Amadeus Demand 360)
      const belongsToMenu = activeMenuObj?.data?.some((item) => {
        if (item.index === ui_state.settingsActiveIndex) return true;
        if (item.isExpandable && item.subItems) {
          return item.subItems.some((sub) => sub.index === ui_state.settingsActiveIndex);
        }
        return false;
      });

      if (!belongsToMenu && firstAccessibleItem) {
        dispatch({
          type: UPDATE_STATE,
          payload: {
            ui_state: {
              ...ui_state,
              active_settings_menu: activeMenuObj.menuId,
              activeSettingsComponentName: firstAccessibleItem.component,
              settingsActiveIndex: firstAccessibleItem.index,
              isFromHeader: false,
            },
          },
        });
      } else {
        // Re-open any expandable parent whose sub-item is currently active
        const toExpand = {};
        activeMenuObj?.data?.forEach((item) => {
          if (item.isExpandable && item.subItems?.some(
            (sub) => sub.index === ui_state.settingsActiveIndex
          )) {
            toExpand[item.id] = true;
          }
        });
        if (Object.keys(toExpand).length > 0) {
          setExpandedItems(toExpand);
        }
      }
    }
  }, [ui_state.active_settings_menu]);

  useEffect(() => {
    const activeMenuObj = settingsSideMenuList.find(m => m.menuId === activeMenu);
    const toExpand = {};
    activeMenuObj?.data?.forEach((item) => {
      if (item.isExpandable && item.subItems?.some(
        (sub) => sub.index === ui_state.settingsActiveIndex
      )) {
        toExpand[item.id] = true;
      }
    });
    setExpandedItems(toExpand);
  }, [activeMenu]);

  if (ui_state.isFromHeader) {
    dispatch({
      type: UPDATE_STATE,
      payload: { ui_state: { isFromHeader: false } },
    });
  }

  const handleListItemClick = (item, isSubItem = false) => (event) => {
    if (handleUnsavedChanges()) return; // Return if unsaved changes exist

    if (item.component === "DefaultRoomTypePricing") {
      window.analyze.track("pricing_seasons_settings", { selected_tile: "Pricing Seasons Settings" });
    }

    if (!isSubItem) setExpandedItems({});

    ui_state.isFromHeader = false;
    ui_state.active_settings_menu = activeMenu;
    ui_state.activeSettingsComponentName = item.component;
    ui_state.settingsActiveIndex = item.index;

    dispatch({
      type: UPDATE_STATE,
      payload: {
        ui_state: {
          isFromHeader: false,
          active_settings_menu: activeMenu,
          activeSettingsComponentName: item.component,
          settingsActiveIndex: item.index,
        },
      },
    });
  };

  const handleMainMenuClick = (menuId) => {
    if (handleUnsavedChanges()) return; // Return if unsaved changes exist

    if (pinnedMenu === menuId) return; // Return if smenu already pinned

    setPinnedMenu(menuId);
    const menu = settingsSideMenuList.find((m) => m.menuId === menuId);
    if (menu && menu.data.length > 0) {
      // if propertySettingsMenu then select Inventory or else select the first item
      const firstItem = menuId==="propertySettingsMenu" ? menu.data[1] : menu.data[0];
      dispatch({
        type: UPDATE_STATE,
        payload: {
          ui_state: {
            ...ui_state,
            active_settings_menu: menuId,
            activeSettingsComponentName: firstItem.component,
            settingsActiveIndex: firstItem.index,
            isFromHeader: false,
          },
        },
      });
    }
  };

  const handleUnsavedChanges = () => {
    const isChanged = localStorage.getItem("isChanged");
    if (isChanged === "true") {
      const shakeBtnsDiv = document.getElementsByClassName("top-axn-bar-1-sub");
      const shakeAlert = document.getElementsByClassName("save-changes-alert");

      // Shake the Save/Cancel button group
      if (shakeBtnsDiv.length > 0) {
        shakeBtnsDiv[0].classList.add("bt-shake");
        setTimeout(() => shakeBtnsDiv[0].classList.remove("bt-shake"), 2000);
      }

      // Show the alert temporarily
      if (shakeAlert.length > 0) {
        shakeAlert[0].classList.remove("display-none");
        setTimeout(() => shakeAlert[0].classList.add("display-none"), 2000);
      }

      return true; // indicates unsaved changes
    }
    return false; // safe to proceed
  };

  const handleTogglePin = () => {
    if (pinnedMenu) {
      setPinnedMenu(null);
    } else {
      const menuId = findMenuIDByIndex();
      setPinnedMenu(menuId);
    }
  };

  function findMenuIDByIndex() {
    for (const menu of settingsSideMenuList) {
      if (menu.data.some(item => item.index === settingsActiveIndex)) {
        return menu.menuId;
      }
    }
    return null;
  }

  return (
    <Grid container className={`settings-container-new setting-sidemenu ${pinnedMenu ? "expanded" : "collapsed"}`}>
      {/* Main Menu Section */}
      <Grid item className="main-menu">
        {settingsSideMenuList.map((menu) => (
          <Box
            key={menu.menuId}
            className={`main-menu-item ${selectedMenu === menu.menuId ? "selected" : ""} ${activeMenu === menu.menuId ? "active" : ""}`}
            onMouseEnter={() => !pinnedMenu && setHoveredMenu(menu.menuId)}
            onMouseLeave={() => {
              if (!pinnedMenu && !isMouseInSubmenu) {
                setHoveredMenu(null);
              }
            }}
            onClick={() => pinnedMenu && handleMainMenuClick(menu.menuId)}
          >
            <Box className="menu-icon">
              {selectedMenu === menu.menuId ? menu.activeIcon || menu.menuIcon : menu.menuIcon}
            </Box>
            <Typography className="menu-title">{menu.title}</Typography>
          </Box>
        ))}
        <Box className="toggle-btn" onClick={handleTogglePin}>
          {pinnedMenu
            ?
            <svg width="18" height="14" viewBox="0 0 18 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16.1923 0H1.80769C1.08846 0 0.5 0.572727 0.5 1.27273V12.7273C0.5 13.4273 1.08846 14 1.80769 14H16.1923C16.9115 14 17.5 13.4273 17.5 12.7273V1.27273C17.5 0.572727 16.9115 0 16.1923 0ZM1.80769 1.27273H5.07692V12.7273H1.80769V1.27273ZM16.1923 12.7273H6.38462V1.27273H16.1923V12.7273Z" fill="#F8F9FA" />
              <path d="M12.6158 10.4045C12.4915 10.4045 12.3673 10.36 12.2692 10.2645L9.72577 7.78909C9.51 7.57909 9.39231 7.29909 9.39231 7C9.39231 6.70091 9.51 6.42091 9.72577 6.21091L12.2692 3.73545C12.4588 3.55091 12.7727 3.55091 12.9623 3.73545C13.1519 3.92 13.1519 4.22545 12.9623 4.41L10.4188 6.88545C10.3796 6.92364 10.3731 6.97455 10.3731 7C10.3731 7.02545 10.3731 7.07 10.4188 7.11455L12.9623 9.59C13.1519 9.77454 13.1519 10.08 12.9623 10.2645C12.8642 10.36 12.74 10.4045 12.6158 10.4045Z" fill="#F8F9FA" />
            </svg>
            :
            <svg width="18" height="14" viewBox="0 0 18 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16.1923 0H1.80769C1.08846 0 0.5 0.572727 0.5 1.27273V12.7273C0.5 13.4273 1.08846 14 1.80769 14H16.1923C16.9115 14 17.5 13.4273 17.5 12.7273V1.27273C17.5 0.572727 16.9115 0 16.1923 0ZM1.80769 1.27273H5.07692V12.7273H1.80769V1.27273ZM16.1923 12.7273H6.38462V1.27273H16.1923V12.7273Z" fill="#F8F9FA" />
              <path d="M9.88269 10.4045C9.75846 10.4045 9.63423 10.36 9.53615 10.2645C9.34654 10.08 9.34654 9.77454 9.53615 9.59L12.0796 7.11455C12.1188 7.07636 12.1254 7.02545 12.1254 7C12.1254 6.97455 12.1254 6.93 12.0796 6.88545L9.53615 4.41C9.34654 4.22545 9.34654 3.92 9.53615 3.73545C9.72577 3.55091 10.0396 3.55091 10.2292 3.73545L12.7727 6.21091C12.9885 6.42091 13.1062 6.70091 13.1062 7C13.1062 7.29909 12.9885 7.57909 12.7727 7.78909L10.2292 10.2645C10.1312 10.36 10.0069 10.4045 9.88269 10.4045Z" fill="#F8F9FA" />
            </svg>
          }
        </Box>
      </Grid>

      {/* Sub Menu Panel Section */}
      <Grid
        item
        className={`submenu-panel ${pinnedMenu ? "fixed" : hoveredMenu ? "open" : "close"}`}
        onMouseEnter={() => {
          setIsMouseInSubmenu(true);
          if (!pinnedMenu && activeMenu) {
            setHoveredMenu(activeMenu)
          }
        }}
        onMouseLeave={() => {
          setIsMouseInSubmenu(false);
          if (!pinnedMenu) {
            setHoveredMenu(null)
          }
        }}
      >
        <nav>
          <List sx={{ maxHeight: "100vh", overflow: "auto", pb: 0 }}>
            {settingsSideMenuList
              .find((m) => m.menuId === activeMenu)?.data
              .filter((item) => item.access && item.access.includes(user_login_type))
              .map((item, index, arr) => {
                const prevGroup = index > 0 ? arr[index - 1].group : null;
                const showGroupHeader = item.group && item.group !== prevGroup;

                const isExpanded = expandedItems[item.id];
                const isParentActive = item.isExpandable && item.subItems?.some(sub => settingsActiveIndex === sub.index);

                return (
                  <React.Fragment key={"submenu-" + index}>
                    {showGroupHeader && (
                      <div className="submenu-group-header">
                        {item.group.charAt(0).toUpperCase() + item.group.slice(1)}
                      </div>
                    )}

                    <div className="menu-item">
                      {item.isExpandable ? (
                        <>
                          <ListItemButton
                            className={`item-container expandable-parent${isParentActive ? " Mui-selected" : ""}`}
                            onClick={() => toggleExpand(item.id)}
                          >
                            <ListItemIcon className="menu-item-icon">{item.icon}</ListItemIcon>
                            <ListItemText
                              className="menu-item-label"
                              primary={<span>{item.label}</span>}
                            />
                            <span className="expand-chevron">
                              {isExpanded
                                ? <svg width="12" height="7" viewBox="0 0 12 7" fill="none"><path d="M11 6L6 1L1 6" stroke="#606060" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                : <svg width="12" height="7" viewBox="0 0 12 7" fill="none"><path d="M1 1L6 6L11 1" stroke="#606060" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                              }
                            </span>
                          </ListItemButton>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <div className="expandable-sub-items">
                              {item.subItems
                                .filter(sub => !sub.access || sub.access.includes(user_login_type))
                                .map((subItem, subIndex) => (
                                  <ListItemButton
                                    key={"sub-" + subIndex}
                                    className={`item-container sub-item-container${subItem.disabled ? " sub-item-disabled" : ""}`}
                                    selected={settingsActiveIndex === subItem.index}
                                    disabled={subItem.disabled}
                                    onClick={!subItem.disabled ? handleListItemClick(subItem, true) : undefined}
                                  >
                                    <ListItemText
                                      className="menu-item-label"
                                      primary={
                                        <span style={{ display: "flex", alignItems: "center", gap: "5px", flexWrap: "nowrap" }}>
                                          <span style={{ fontSize: "12px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                            {subItem.label}
                                            {subItem.subLabel && <span style={{ color: "#888", marginLeft: "2px" }}>{subItem.subLabel}</span>}
                                          </span>
                                          {subItem.badge && (
                                            <span className="menu-item-new-badge" style={{ top: 0, flexShrink: 0 }}>
                                              {subItem.badge}
                                            </span>
                                          )}
                                        </span>
                                      }
                                    />
                                  </ListItemButton>
                                ))}
                            </div>
                          </Collapse>
                        </>
                      ) : (
                        <ListItemButton
                          className="item-container"
                          selected={settingsActiveIndex === item.index}
                          onClick={handleListItemClick(item)}
                        >
                          <ListItemIcon className="menu-item-icon">{item.icon}</ListItemIcon>
                          <ListItemText
                            className="menu-item-label"
                            primary={
                              <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                {item.label}
                                {item.badge && <span className="menu-item-new-badge">{item.badge}</span>}
                              </span>
                            }
                          />
                        </ListItemButton>
                      )}
                      <Divider sx={{ display: item.isLast ? "none" : "block" }} />
                    </div>
                  </React.Fragment>
                );
              })}
          </List>
        </nav>
      </Grid>

    </Grid>
  );
}

export default SettingsSideMenu;
